import { SchoolProperty, SchoolPropertyCategory } from '@schooly/api';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';

import {
  isSchoolTuneStatus,
  SchoolTuneStatus,
  SchoolTuneStatusesForm,
  SchoolTuneStatusType,
  StatusCategoryType,
} from './scheme';

export const getEmptyStatus = (category: SchoolPropertyCategory): SchoolTuneStatus => ({
  id: '',
  originId: '',
  replaceId: '',
  name: '',
  type: SchoolPropertyType.Status,
  conduct_default: false,
  group_default: false,
  student_default: false,
  staff_default: false,
  archived: false,
  order: 0,
  category,
});

export const getSubCategories = (categoryId: string, categories: SchoolPropertyCategory[]) => {
  return categories.filter((c) => c.parent_category_id === categoryId);
};

export const getCategoryTypeFromName = (categoryName: string): StatusCategoryType | undefined => {
  switch (categoryName) {
    case 'Prospective':
      return 'prospective';
    case 'Applied':
      return 'applied';
    case 'Offered':
      return 'offered';
    case 'Accepted':
      return 'accepted';
    case 'Current':
      return 'current';
    case 'Former':
      return 'former';
    case 'Unsuccessful':
      return 'unsuccessful';
    default:
      return undefined;
  }
};

export const getSortedCategories = (categories: SchoolPropertyCategory[]) => {
  const [parents, children] = categories.reduce<
    [SchoolPropertyCategory[], SchoolPropertyCategory[]]
  >(
    (prev, category) => {
      prev[category.parent_category_id ? 1 : 0].push(category);
      return prev;
    },
    [[], []],
  );
  const sortedParents = sortByOrder(parents);

  const result: SchoolPropertyCategory[] = [];
  sortedParents.forEach((parent) => {
    result.push(parent);
    const parentChildren = children.filter((c) => c.parent_category_id === parent.id);
    const sortedChildren = sortByOrder(parentChildren);
    result.push(...sortedChildren);
  });

  return result;
};

export const sortByOrder = <T extends { order: number }>(items: T[]): T[] => {
  return items.sort((a, b) => a.order - b.order);
};

type CategoriesByUserType = {
  staff: SchoolPropertyCategory[];
  student: SchoolPropertyCategory[];
};

export const getCategoriesByUserType = (
  categories: SchoolPropertyCategory[],
): CategoriesByUserType => {
  return categories.reduce<CategoriesByUserType>(
    (prev, category) => {
      switch (category.user_type) {
        case SchoolUserRole.Staff:
          prev.staff.push(category);
          break;
        case SchoolUserRole.Student:
          prev.student.push(category);
          break;
      }
      return prev;
    },
    { staff: [], student: [] },
  );
};

export const getCategoriesByType = (categories: SchoolPropertyCategory[]) => {
  return categories.reduce<Partial<Record<StatusCategoryType, SchoolPropertyCategory>>>(
    (prev, category) => {
      const type = getCategoryTypeFromName(category.name);
      if (type) {
        prev[type] = category;
      }
      return prev;
    },
    {},
  );
};

const getCategoryById = (id: string, categories: SchoolPropertyCategory[]) => {
  return categories.find((category) => category.id === id);
};

export const getStatusesByParentCategory = (
  statuses: SchoolProperty[],
  categories: SchoolPropertyCategory[],
) => {
  return statuses.reduce<SchoolTuneStatusType>((prev, status) => {
    let category = status.category;

    if (category?.parent_category_id) {
      category = getCategoryById(category.parent_category_id, categories);
    }

    if (!category) {
      return prev;
    }

    const type = getCategoryTypeFromName(category.name);

    if (!type) {
      return prev;
    }

    if (!prev[type]) {
      prev[type] = [];
    }

    prev[type]?.push(status);
    return prev;
  }, {});
};

export const getFormStatuses = (statuses: SchoolProperty[]): SchoolTuneStatus[] => {
  return statuses.filter(isSchoolTuneStatus).map((status) => ({ ...status, originId: status.id }));
};

type PrepareStatusesToSave<T> = {
  categories: SchoolPropertyCategory[];
  data: SchoolTuneStatusesForm;
  predicate: (
    categoryId: string,
    index: number,
  ) => (status: SchoolTuneStatus, index: number, array: SchoolTuneStatus[]) => T;
};

export const prepareStatusesToSave = <T,>({
  categories,
  data,
  predicate,
}: PrepareStatusesToSave<T>) => {
  const sortedCategories = getSortedCategories(categories);
  const { staff, student } = getCategoriesByUserType(sortedCategories);

  const statuses: T[] = [];

  (['students', 'staff'] as (keyof SchoolTuneStatusesForm)[]).forEach((type) => {
    let index = 0;

    const userTypeCategories = type === 'staff' ? staff : student;

    for (const category of userTypeCategories) {
      if (!category.id) {
        continue;
      }

      const categoryStatuses = data[type].filter(
        (status) => status.category?.name === category.name,
      );

      statuses.push(...categoryStatuses.map(predicate(category.id, index)));

      index += categoryStatuses.length;
    }
  });

  return statuses;
};
